import { useState, useEffect } from 'react';
import { 
  Users, Megaphone, LogOut, LayoutDashboard, Settings, 
  DollarSign, Activity, Eye, Trash2, CheckCircle2,
  ChevronLeft, ChevronRight, Sun, Moon, ChevronDown,
  BarChart3, Download, Target
} from 'lucide-react';
import InfluencerDatabase from './InfluencerDatabase';
import InfluencerCampaigns from './InfluencerCampaigns';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { formatNumber, formatCurrency } from '../utils/formatters';
import { toast } from 'sonner';

const InfluencerMarketing = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const { logout, user } = useAuth();
  const userRole = user?.role || 'admin'; // 'admin' | 'user' | 'viewer'

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', next);
      return next;
    });
  };

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-salty-bg text-salty-text-main flex flex-row">
      {/* Left Sidebar */}
      <aside className={`bg-white dark:bg-[#151e2e] border-r border-slate-200 dark:border-white/10 flex flex-col justify-between h-screen sticky top-0 shrink-0 select-none transition-all duration-300 ease-in-out ${isCollapsed ? 'w-16' : 'w-64'}`}>
        <div className="flex flex-col">
          {/* Brand Logo & Collapse Toggle */}
          <div className={`h-[72px] flex items-center border-b border-slate-200 dark:border-white/10 px-4 transition-all duration-300 shrink-0 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
            <div
              className={`flex items-center gap-3 overflow-hidden cursor-pointer ${isCollapsed ? 'justify-center w-full' : ''}`}
              onClick={isCollapsed ? toggleCollapse : undefined}
              title={isCollapsed ? "Expand Sidebar" : ""}
            >
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#1264d1] to-[#0284c7] flex items-center justify-center text-white shadow-sm shrink-0">
                <Target size={18} />
              </div>
              {!isCollapsed && (
                <div className="flex flex-col overflow-hidden">
                  <span className="font-bold text-sm tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-500 dark:from-white dark:via-slate-100 dark:to-slate-400 bg-clip-text text-transparent whitespace-nowrap leading-tight truncate">
                    Influencer Marketing
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">Campaign & Creator Platform</span>
                </div>
              )}
            </div>
            {!isCollapsed && (
              <button
                onClick={toggleCollapse}
                className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors shrink-0 cursor-pointer"
                title="Collapse Sidebar"
              >
                <ChevronLeft size={16} />
              </button>
            )}
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { id: 'database', label: 'Influencer Database', icon: Users },
              { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
              // Reports: admin only
              ...(userRole === 'admin' ? [{ id: 'reports', label: 'Reports', icon: BarChart3 }] : []),
              { id: 'settings', label: 'Settings', icon: Settings },
            ].map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center rounded-xl transition-all duration-200 group font-medium w-full ${
                    isCollapsed ? 'justify-center p-3' : 'gap-3 px-3.5 py-2.5 text-sm font-semibold'
                  } ${
                    isActive
                      ? 'bg-[#1264d1]/10 text-[#1264d1] dark:text-[#3b82f6]'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5'
                  }`}
                  title={isCollapsed ? item.label : ''}
                  data-testid={`tab-${item.id}`}
                >
                  <Icon
                    size={18}
                    className={`shrink-0 ${
                      isActive
                        ? 'text-[#1264d1] dark:text-[#3b82f6]'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200'
                    }`}
                  />
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </button>
              );
            })}
          </nav>
        </div>

        {/* User profile & logout at bottom */}
        <div className={`p-4 border-t border-slate-200 dark:border-white/10 flex flex-col gap-2 shrink-0 ${isCollapsed ? 'items-center' : ''}`}>
          <button
            onClick={logout}
            className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800/60 hover:bg-rose-500/10 text-slate-500 dark:text-slate-400 hover:text-rose-600 border border-transparent rounded-xl transition-all cursor-pointer ${
              isCollapsed ? 'p-2.5 w-9 h-9' : 'w-full gap-2 px-4 py-2.5 text-xs font-semibold'
            }`}
            title="Log out"
          >
            <LogOut size={14} className="shrink-0" />
            {!isCollapsed && 'Log Out'}
          </button>
        </div>
      </aside>

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="h-[72px] flex items-center justify-between px-8 bg-salty-card border-b border-salty-border">
          <div>
            <h1 className="text-xl font-bold text-salty-text-main capitalize">
              {activeTab === 'database' ? 'Influencer Database' : activeTab === 'reports' ? 'Reports Center' : activeTab}
            </h1>
            <p className="text-[10.5px] text-salty-text-muted mt-0.5 leading-none">
              {activeTab === 'dashboard' && 'Platform metrics, campaign health, and active ROI tracker'}
              {activeTab === 'database' && 'Manage your live influencer network, rates, and historical logs'}
              {activeTab === 'campaigns' && 'Monitor influencer campaign schedules, content, and real-time reach'}
              {activeTab === 'reports' && 'Generate, customize, and export detailed campaign performance and influencer registry reports'}
              {activeTab === 'settings' && 'Database management, platform API keys, and configurations'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Theme Toggle Button */}
            <button 
              onClick={toggleTheme} 
              className="w-10 h-10 rounded-xl border border-salty-border bg-salty-card hover:bg-salty-subtle transition-all flex items-center justify-center text-salty-purple shadow-sm cursor-pointer"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
            </button>

            {/* User Profile Dropdown */}
            <div className="relative">
              <div 
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-salty-subtle transition-all cursor-pointer select-none"
              >
                <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-sm shadow-sm shrink-0">
                  {user?.name?.charAt(0).toUpperCase() || 'R'}
                </div>
                <div className="hidden sm:flex flex-col text-left">
                  <span className="font-bold text-salty-text-main text-sm leading-tight">{user?.name || 'Rishabh'}</span>
                  <span className={`px-1.5 py-0.5 rounded border text-[9px] font-black uppercase tracking-wider w-fit mt-0.5 leading-none ${
                    userRole === 'admin'
                      ? 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-500'
                      : userRole === 'user'
                      ? 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400'
                      : 'bg-slate-500/10 border-slate-500/20 text-slate-500'
                  }`}>
                    {userRole.toUpperCase()}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-salty-text-muted shrink-0" />
              </div>

              {/* Profile Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-salty-card border border-salty-border rounded-2xl shadow-xl py-2 z-50 animate-fadeIn">
                  <div className="px-4 py-2 border-b border-salty-border">
                    <p className="text-xs text-salty-text-muted">Signed in as</p>
                    <p className="text-sm font-bold text-salty-text-main truncate">{user?.email || 'admin@example.com'}</p>
                  </div>
                  <button
                    onClick={() => {
                      setIsProfileDropdownOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-salty-text-muted hover:text-rose-600 hover:bg-rose-500/5 text-left transition-all font-semibold"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Pane */}
        <main className="flex-1 p-8 overflow-y-auto w-full">
          {activeTab === 'dashboard' && <DashboardView />}
          {activeTab === 'database'  && <InfluencerDatabase  userRole={userRole} />}
          {activeTab === 'campaigns' && <InfluencerCampaigns userRole={userRole} />}
          {activeTab === 'reports'   && userRole === 'admin' && <ReportsView />}
          {activeTab === 'reports'   && userRole !== 'admin' && (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
              <div className="text-4xl">🔒</div>
              <h3 className="text-lg font-bold text-salty-text-main">Reports — Admin Only</h3>
              <p className="text-sm text-salty-text-muted max-w-sm">Reports are restricted to admin users. Contact your administrator for access.</p>
            </div>
          )}
          {activeTab === 'settings'  && <SettingsView user={user} />}
        </main>
      </div>
    </div>
  );
};

// ── SUB-COMPONENT: DASHBOARD VIEW ──
const DashboardView = () => {
  const [stats, setStats] = useState(null);
  const [campStats, setCampStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const [infRes, campRes] = await Promise.all([
          api.getInfluencerStats(),
          api.getCampaignStats()
        ]);
        setStats(infRes.data);
        setCampStats(campRes.data);
      } catch (err) {
        console.error("Error loading stats:", err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-salty-purple border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm text-salty-text-muted mt-4">Loading stats and analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm hover:shadow-md transition-all flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-semibold text-salty-text-muted uppercase tracking-wider">Total Network</span>
            <h3 className="text-3xl font-bold text-salty-text-main">
              {stats?.total_influencers || 0}
            </h3>
            <span className="text-[10px] text-emerald-500 font-medium">
              {stats?.active_influencers || 0} Active Profiles
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600">
            <Users className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm hover:shadow-md transition-all flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-semibold text-salty-text-muted uppercase tracking-wider">Campaigns</span>
            <h3 className="text-3xl font-bold text-salty-text-main">
              {campStats?.total_campaigns || 0}
            </h3>
            <span className="text-[10px] text-salty-purple font-medium">
              {campStats?.active_campaigns || 0} In Progress
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-600">
            <Megaphone className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm hover:shadow-md transition-all flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-semibold text-salty-text-muted uppercase tracking-wider">Total Reach</span>
            <h3 className="text-3xl font-bold text-salty-text-main">
              {formatNumber(campStats?.total_reach || 0)}
            </h3>
            <span className="text-[10px] text-teal-500 font-medium">
              Across all live content
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-600">
            <Activity className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm hover:shadow-md transition-all flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs font-semibold text-salty-text-muted uppercase tracking-wider">Spent / Budget</span>
            <h3 className="text-xl font-bold text-salty-text-main leading-tight">
              {formatCurrency(campStats?.total_spent || 0)}
            </h3>
            <span className="text-[10px] text-salty-text-muted block mt-0.5">
              Budget: {formatCurrency(campStats?.total_budget || 0)}
            </span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600">
            <DollarSign className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Row 2: Charts and Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Engagement Performance Card */}
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm flex flex-col justify-between">
          <div>
            <h4 className="text-xs font-bold text-salty-text-muted mb-4 uppercase tracking-wider">Engagement Performance</h4>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-black text-salty-purple tracking-tight">
                {campStats?.total_reach > 0 
                  ? ((campStats.total_engagement / campStats.total_reach) * 100).toFixed(2)
                  : '0.00'}%
              </span>
              <span className="text-xs text-salty-text-muted">Engagement Rate</span>
            </div>
            <p className="text-xs text-salty-text-muted mt-3 leading-relaxed">
              Total Engagements: <strong className="text-salty-text-main">{formatNumber(campStats?.total_engagement || 0)}</strong> across campaigns.
            </p>
          </div>
          <div className="mt-6 pt-4 border-t border-salty-border flex items-center justify-between text-xs">
            <span className="text-salty-text-muted">Cost per Engagement (CPE)</span>
            <span className="font-semibold text-salty-text-main">
              {campStats?.total_engagement > 0
                ? formatCurrency(campStats.total_spent / campStats.total_engagement)
                : '—'}
            </span>
          </div>
        </div>

        {/* Tier Distribution Card */}
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm">
          <h4 className="text-xs font-bold text-salty-text-muted mb-4 uppercase tracking-wider">Influencer Tiers</h4>
          <div className="space-y-3.5">
            {['mega', 'macro', 'mid', 'micro', 'nano'].map(tier => {
              const count = stats?.by_tier?.[tier] || 0;
              const total = stats?.total_influencers || 1;
              const percent = Math.round((count / total) * 100);
              return (
                <div key={tier} className="space-y-1">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="capitalize text-salty-text-main">{tier}</span>
                    <span className="text-salty-text-muted">{count} ({percent}%)</span>
                  </div>
                  <div className="w-full h-2 bg-salty-subtle rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-salty-purple rounded-full" 
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Category Breakdown Card */}
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm flex flex-col justify-between">
          <div>
            <h4 className="text-xs font-bold text-salty-text-muted mb-4 uppercase tracking-wider">Top Categories</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats?.by_category || {}).length === 0 ? (
                <p className="text-xs text-salty-text-muted">No categories recorded yet.</p>
              ) : (
                Object.entries(stats?.by_category || {})
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([cat, count]) => (
                    <div key={cat} className="flex items-center gap-1.5 px-3 py-1.5 bg-salty-subtle rounded-xl border border-salty-border text-xs font-medium text-salty-text-main">
                      <span className="capitalize">{cat}</span>
                      <span className="px-1.5 py-0.5 bg-salty-card rounded-md border border-salty-border text-[10px] font-bold text-salty-purple">{count}</span>
                    </div>
                  ))
              )}
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-salty-border flex items-center justify-between text-xs">
            <span className="text-salty-text-muted">Total Engagement</span>
            <span className="font-semibold text-emerald-500">+{formatNumber(campStats?.total_engagement || 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── SUB-COMPONENT: REPORTS VIEW ──
const ReportsView = () => {
  const [reportLoading, setReportLoading] = useState(false);
  const [campaignReportType, setCampaignReportType] = useState('summary');
  const [influencerReportType, setInfluencerReportType] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const downloadCSV = (headers, rows, filename) => {
    const content = [
      headers.join(','),
      ...rows.map(row => row.map(val => {
        const stringVal = val === null || val === undefined ? '' : String(val);
        const escaped = stringVal.replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(','))
    ].join('\n');

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCampaignReportExport = async () => {
    try {
      setReportLoading(true);
      const res = await api.getInfluencerCampaigns({ limit: 1000 });
      const campaigns = res.data?.campaigns || [];

      if (campaigns.length === 0) {
        toast.error("No campaign records found to export.");
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];

      if (campaignReportType === 'summary') {
        const headers = ["Campaign ID", "Campaign Name", "Status", "Budget (INR)", "Spent (INR)", "Reach", "Engagement", "Start Date", "End Date"];
        const rows = campaigns.map(c => [
          c.id || c._id || '',
          c.name || '',
          c.status || '',
          c.budget || 0,
          c.budget_spent || 0,
          c.total_reach || 0,
          c.total_engagement || 0,
          c.start_date || '',
          c.end_date || ''
        ]);
        downloadCSV(headers, rows, `campaigns_performance_summary_${timestamp}.csv`);
        toast.success("Campaign performance summary downloaded successfully!");

      } else if (campaignReportType === 'monthly') {
        const [year, month] = selectedMonth.split('-');
        const targetMonthStart = new Date(year, month - 1, 1);
        const targetMonthEnd = new Date(year, month, 0);

        const filtered = campaigns.filter(c => {
          if (!c.start_date) return false;
          const start = new Date(c.start_date);
          return start >= targetMonthStart && start <= targetMonthEnd;
        });

        if (filtered.length === 0) {
          toast.warning(`No campaigns started in ${selectedMonth}. Downloading all campaigns instead.`);
        }

        const headers = ["Campaign Name", "Month", "Budget (INR)", "Spent (INR)", "Total Reach", "Total Engagement"];
        const listToExport = filtered.length > 0 ? filtered : campaigns;
        const rows = listToExport.map(c => [
          c.name || '',
          selectedMonth,
          c.budget || 0,
          c.budget_spent || 0,
          c.total_reach || 0,
          c.total_engagement || 0
        ]);
        downloadCSV(headers, rows, `campaigns_monthly_audit_${selectedMonth}.csv`);
        toast.success(`Monthly audit report for ${selectedMonth} downloaded!`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate campaign report.");
    } finally {
      setReportLoading(false);
    }
  };

  const handleInfluencerReportExport = async () => {
    try {
      setReportLoading(true);
      const [res, trackerRes] = await Promise.all([
        api.getInfluencers({ limit: 1000 }),
        api.getSavedTrackers()
      ]);
      const influencers = res.data?.influencers || [];
      if (influencers.length === 0) {
        toast.error("No influencer records found to export.");
        return;
      }

      // Map trackers by lowercased clean handle
      const trackersMap = trackerRes.data?.influencers || {};

      const timestamp = new Date().toISOString().split('T')[0];

      // Helper to extract clean metric data
      const resolveInfluencerMetrics = (i) => {
        const handle = i.instagram_handle?.replace(/^@+/g, '').toLowerCase();
        const tracker = trackersMap[handle];
        const followers = tracker?.profile?.followers || i.followers_count || 0;
        const avgViews = tracker?.computed?.avgReelViews || i.avg_views || i.youtube_avg_views || 0;
        const engagementRate = tracker?.computed?.engagementRate || i.engagement_rate || 0;
        return { followers, avgViews, engagementRate };
      };

      if (influencerReportType === 'all') {
        const headers = ["Influencer ID", "Full Name", "Handle", "Email", "Platform", "Tier", "Category", "FollowersCount", "EngagementRate (%)", "AvgViews", "AgreedFee (INR)", "Status", "Source"];
        const rows = influencers.map(i => {
          const metrics = resolveInfluencerMetrics(i);
          return [
            i.id || i._id || '',
            i.name || '',
            i.instagram_handle || '',
            i.email || '',
            i.platform || 'Instagram',
            i.tier || '',
            i.category || '',
            metrics.followers,
            metrics.engagementRate,
            metrics.avgViews,
            i.agreed_fee || 0,
            i.status || '',
            i.source || ''
          ];
        });
        downloadCSV(headers, rows, `influencers_master_directory_${timestamp}.csv`);
        toast.success("Master influencer database downloaded!");

      } else if (influencerReportType === 'leaderboard') {
        const resolvedList = influencers.map(i => {
          const metrics = resolveInfluencerMetrics(i);
          return { ...i, ...metrics };
        });
        const sorted = resolvedList.sort((a, b) => b.followers - a.followers);
        const headers = ["Rank", "Influencer Name", "Instagram Handle", "Followers", "Engagement Rate (%)", "Average Views", "Status"];
        const rows = sorted.map((i, index) => [
          index + 1,
          i.name || '',
          i.instagram_handle || '',
          i.followers,
          i.engagementRate,
          i.avgViews,
          i.status || ''
        ]);
        downloadCSV(headers, rows, `influencers_reach_leaderboard_${timestamp}.csv`);
        toast.success("Influencer reach leaderboard downloaded!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate influencer report.");
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="space-y-8 w-full animate-fadeIn">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Campaign Reports Generator */}
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm space-y-6 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-salty-purple/10 text-salty-purple flex items-center justify-center">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-salty-text-main">Campaign Analytics Reports</h3>
                <p className="text-xs text-salty-text-muted mt-0.5">Export detailed campaigns data, budget sheets, and post metrics logs</p>
              </div>
            </div>

            <div className="space-y-3.5 pt-4">
              <label className="text-xs font-semibold text-salty-text-muted">Select Report Scope</label>
              <div className="grid grid-cols-1 gap-2.5">
                <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${campaignReportType === 'summary' ? 'bg-salty-purple/5 border-salty-purple/35' : 'bg-salty-subtle/30 border-salty-border/60 hover:bg-salty-subtle/50'}`}>
                  <input 
                    type="radio" 
                    name="campaignReport" 
                    value="summary" 
                    checked={campaignReportType === 'summary'} 
                    onChange={(e) => setCampaignReportType(e.target.value)}
                    className="mt-0.5 accent-salty-purple"
                  />
                  <div>
                    <span className="text-xs font-bold text-salty-text-main block">Campaigns Performance Summary</span>
                    <span className="text-[10.5px] text-salty-text-muted mt-0.5 block">Full rollup of metrics, budget spent, and revenues across all campaign folders.</span>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${campaignReportType === 'monthly' ? 'bg-salty-purple/5 border-salty-purple/35' : 'bg-salty-subtle/30 border-salty-border/60 hover:bg-salty-subtle/50'}`}>
                  <input 
                    type="radio" 
                    name="campaignReport" 
                    value="monthly" 
                    checked={campaignReportType === 'monthly'} 
                    onChange={(e) => setCampaignReportType(e.target.value)}
                    className="mt-0.5 accent-salty-purple"
                  />
                  <div>
                    <span className="text-xs font-bold text-salty-text-main block">Monthly Campaigns Financial Audit</span>
                    <span className="text-[10.5px] text-salty-text-muted mt-0.5 block">Audit spend and ROI results aggregated by target calendar month.</span>
                  </div>
                </label>
              </div>

              {campaignReportType === 'monthly' && (
                <div className="space-y-1.5 pt-2 animate-fadeIn">
                  <label className="text-[11px] font-bold text-salty-text-muted uppercase tracking-wider">Select Target Month</label>
                  <input 
                    type="month" 
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="pt-6 border-t border-salty-border flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-md">Excel / CSV Ready</span>
            <button
              disabled={reportLoading}
              onClick={handleCampaignReportExport}
              className="flex items-center gap-2 px-5 py-2.5 bg-salty-purple hover:bg-salty-purple-light text-white text-xs font-bold rounded-xl transition-all shadow-sm disabled:opacity-50 cursor-pointer"
            >
              {reportLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Download Report
            </button>
          </div>
        </div>

        {/* Influencer Registry Exporter */}
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm space-y-6 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-salty-purple/10 text-salty-purple flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-salty-text-main">Influencer Registry Exports</h3>
                <p className="text-xs text-salty-text-muted mt-0.5">Export master listings, engagement benchmarks, and contact databases</p>
              </div>
            </div>

            <div className="space-y-3.5 pt-4">
              <label className="text-xs font-semibold text-salty-text-muted">Select Report Scope</label>
              <div className="grid grid-cols-1 gap-2.5">
                <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${influencerReportType === 'all' ? 'bg-salty-purple/5 border-salty-purple/35' : 'bg-salty-subtle/30 border-salty-border/60 hover:bg-salty-subtle/50'}`}>
                  <input 
                    type="radio" 
                    name="influencerReport" 
                    value="all" 
                    checked={influencerReportType === 'all'} 
                    onChange={(e) => setInfluencerReportType(e.target.value)}
                    className="mt-0.5 accent-salty-purple"
                  />
                  <div>
                    <span className="text-xs font-bold text-salty-text-main block">Master Database Export</span>
                    <span className="text-[10.5px] text-salty-text-muted mt-0.5 block">Full export of the network database, handles, emails, categories, and agreed fees.</span>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${influencerReportType === 'leaderboard' ? 'bg-salty-purple/5 border-salty-purple/35' : 'bg-salty-subtle/30 border-salty-border/60 hover:bg-salty-subtle/50'}`}>
                  <input 
                    type="radio" 
                    name="influencerReport" 
                    value="leaderboard" 
                    checked={influencerReportType === 'leaderboard'} 
                    onChange={(e) => setInfluencerReportType(e.target.value)}
                    className="mt-0.5 accent-salty-purple"
                  />
                  <div>
                    <span className="text-xs font-bold text-salty-text-main block">Reach & Audience Leaderboard</span>
                    <span className="text-[10.5px] text-salty-text-muted mt-0.5 block">Rank influencers by follower footprint, engagement rate, and visibility scores.</span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-salty-border flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-md">Excel / CSV Ready</span>
            <button
              disabled={reportLoading}
              onClick={handleInfluencerReportExport}
              className="flex items-center gap-2 px-5 py-2.5 bg-salty-purple hover:bg-salty-purple-light text-white text-xs font-bold rounded-xl transition-all shadow-sm disabled:opacity-50 cursor-pointer"
            >
              {reportLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Download Report
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

// ── SUB-COMPONENT: SETTINGS VIEW ──
const SettingsView = ({ user }) => {
  const [actionLoading, setActionLoading] = useState(false);
  
  // Password Reset States
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // User Creation States
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');

  // Users List State
  const [usersList, setUsersList] = useState([]);

  // User Editing States
  const [editingUser, setEditingUser] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserRole, setEditUserRole] = useState('user');
  const [editUserPassword, setEditUserPassword] = useState('');

  const loadUsers = async () => {
    if (user?.role === 'admin' || user?.role === 'owner') {
      try {
        const res = await api.getUsers();
        setUsersList(res.data || []);
      } catch (err) {
        console.error("Error loading users:", err);
      }
    }
  };

  useEffect(() => {
    loadUsers();
  }, [user]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match!");
      return;
    }
    try {
      setActionLoading(true);
      await api.resetPassword(currentPassword, newPassword);
      toast.success("Password reset successfully!");
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to reset password.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await api.registerUser({
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole
      });
      toast.success("User created successfully!");
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('user');
      loadUsers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to create user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      setActionLoading(true);
      
      // Update name and role
      await api.updateUser(editingUser.email, {
        name: editUserName,
        role: editUserRole
      });

      // Reset password if field is populated
      if (editUserPassword.trim()) {
        await api.resetUserPassword(editingUser.email, editUserPassword.trim());
      }

      toast.success("User updated successfully!");
      setEditingUser(null);
      setEditUserPassword('');
      loadUsers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to update user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (email) => {
    if (email === user?.email) {
      toast.error("You cannot delete your own account!");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete user ${email}?`)) {
      return;
    }
    try {
      setActionLoading(true);
      await api.deleteUser(email);
      toast.success("User deleted successfully!");
      loadUsers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to delete user.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-8 w-full animate-fadeIn">
      {/* User Profile */}
      <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm space-y-4">
        <h3 className="text-base font-bold text-salty-text-main border-b border-salty-border pb-3">User Profile</h3>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-salty-purple flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-salty-purple/20 shrink-0 uppercase select-none">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div>
            <h4 className="font-bold text-salty-text-main text-base leading-tight">{user?.name}</h4>
            <p className="text-xs text-salty-text-muted mt-1">{user?.email}</p>
            <span className="inline-block px-2.5 py-0.5 bg-salty-purple/10 border border-salty-purple/20 text-salty-purple text-[10px] font-bold rounded-full capitalize mt-2.5">
              Role: {user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Password and team management for SSO users */}
      {user?.qq_token ? (
        <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm space-y-3">
          <h3 className="text-base font-bold text-salty-text-main border-b border-salty-border pb-3">Account & Team Settings</h3>
          <p className="text-sm text-salty-text-muted leading-relaxed">
            Your account is managed centrally via the QuirkyQode Portal. Team member permissions and password settings can be configured from your central <a href={process.env.REACT_APP_QQ_PORTAL_URL || "http://localhost:7001/my-tools"} className="text-salty-purple-light hover:underline font-semibold">QuirkyQode Client Dashboard</a>.
          </p>
        </div>
      ) : (
        <>
          {/* Password Reset */}
          <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm space-y-4">
            <h3 className="text-base font-bold text-salty-text-main border-b border-salty-border pb-3">Reset Password</h3>
            <form onSubmit={handlePasswordReset} className="max-w-md space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-salty-text-muted">Current Password</label>
                <input 
                  type="password" 
                  required
                  value={currentPassword} 
                  onChange={(e) => setCurrentPassword(e.target.value)} 
                  className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-salty-text-muted">New Password</label>
                <input 
                  type="password" 
                  required
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-salty-text-muted">Confirm New Password</label>
                <input 
                  type="password" 
                  required
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                  placeholder="Confirm new password"
                />
              </div>
              <button 
                type="submit" 
                disabled={actionLoading}
                className="px-5 py-2.5 bg-salty-purple hover:bg-salty-purple-light text-white text-xs font-bold rounded-xl transition-all shadow-sm shadow-salty-purple/10 disabled:opacity-50 cursor-pointer"
              >
                {actionLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>

          {/* User Management Section (Admin / Owner Only) */}
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <div className="bg-salty-card border border-salty-border p-6 rounded-2xl shadow-sm space-y-6">
              <h3 className="text-base font-bold text-salty-text-main border-b border-salty-border pb-3">User Management</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Create User Form */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-salty-text-main">Create New User</h4>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-salty-text-muted">Full Name</label>
                      <input 
                        type="text" 
                        required
                        value={newUserName} 
                        onChange={(e) => setNewUserName(e.target.value)} 
                        className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                        placeholder="e.g. Jane Doe"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-salty-text-muted">Email Address</label>
                      <input 
                        type="email" 
                        required
                        value={newUserEmail} 
                        onChange={(e) => setNewUserEmail(e.target.value)} 
                        className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                        placeholder="e.g. jane@company.com"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-salty-text-muted">Password</label>
                      <input 
                        type="password" 
                        required
                        value={newUserPassword} 
                        onChange={(e) => setNewUserPassword(e.target.value)} 
                        placeholder="Minimum 6 characters"
                        className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-salty-text-muted">Role</label>
                      <select 
                        value={newUserRole} 
                        onChange={(e) => setNewUserRole(e.target.value)}
                        className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <button 
                      type="submit" 
                      disabled={actionLoading}
                      className="px-5 py-2.5 bg-salty-purple hover:bg-salty-purple-light text-white text-xs font-bold rounded-xl transition-all shadow-sm shadow-salty-purple/10 disabled:opacity-50 cursor-pointer"
                    >
                      {actionLoading ? 'Creating...' : 'Create User'}
                    </button>
                  </form>
                </div>

                {/* Edit User Form */}
                {editingUser && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-salty-text-main">Edit User: {editingUser.email}</h4>
                    <form onSubmit={handleEditUser} className="space-y-4">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-salty-text-muted">Full Name</label>
                        <input 
                          type="text" 
                          required
                          value={editUserName} 
                          onChange={(e) => setEditUserName(e.target.value)} 
                          className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-salty-text-muted">Role</label>
                        <select 
                          value={editUserRole} 
                          onChange={(e) => setEditUserRole(e.target.value)}
                          className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-salty-text-muted">New Password (leave blank to keep current)</label>
                        <input 
                          type="password"
                          value={editUserPassword} 
                          onChange={(e) => setEditUserPassword(e.target.value)} 
                          placeholder="Minimum 6 characters"
                          className="w-full bg-salty-bg border border-salty-border rounded-xl px-4 py-2.5 text-sm text-salty-text-main focus:outline-none focus:ring-1 focus:ring-salty-purple"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <button 
                          type="submit" 
                          disabled={actionLoading}
                          className="px-4 py-2 bg-salty-purple hover:bg-salty-purple-light text-white text-xs font-bold rounded-xl transition-all disabled:opacity-50 cursor-pointer"
                        >
                          Save Changes
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setEditingUser(null)}
                          className="px-4 py-2 bg-salty-subtle text-salty-text-main text-xs font-bold rounded-xl transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-salty-border/60 text-salty-text-muted font-bold">
                      <th className="py-3 px-4">User</th>
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Role</th>
                      <th className="py-3 px-4">Joined</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map(u => (
                      <tr key={u.email} className="border-b border-salty-border/40 hover:bg-salty-subtle/30 transition-colors">
                        <td className="py-3 px-4 font-semibold text-salty-text-main">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-salty-purple/10 text-salty-purple flex items-center justify-center font-bold text-[10px] uppercase shadow-sm">
                              {u.name?.charAt(0) || 'U'}
                            </div>
                            <span>{u.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-salty-text-muted">{u.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${u.role === 'admin' || u.role === 'owner' ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20' : 'bg-salty-purple/10 text-salty-purple border border-salty-purple/20'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-salty-text-muted">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className="py-3 px-4 text-right space-x-2">
                          <button 
                            onClick={() => {
                              setEditingUser(u);
                              setEditUserName(u.name || '');
                              setEditUserRole(u.role || 'user');
                            }}
                            className="px-2.5 py-1.5 border border-salty-border text-salty-text-muted hover:text-salty-purple hover:border-salty-purple/40 rounded-lg font-bold transition-all cursor-pointer"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteUser(u.email)}
                            disabled={u.email === user?.email}
                            className="px-2.5 py-1.5 border border-red-500/20 text-red-500 hover:bg-red-500/5 rounded-lg font-bold transition-all disabled:opacity-30 cursor-pointer"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InfluencerMarketing;

